import { useQuery } from 'react-query'
import { FileText, RefreshCw } from 'lucide-react'
import { obterNfsMomentum } from '../services/consultaNfMomentumService'

function formatarCnpj(value: number): string {
  const s = value.toString().padStart(14, '0')
  return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

function formatarMoeda(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatarData(value: string | null): string {
  if (!value) return '—'
  // "YYYY-MM-DD" sem Z: tratar como data local para evitar -1 dia em fusos UTC-
  const [year, month, day] = value.split('-').map(Number)
  const d = new Date(year, month - 1, day)
  return d.toLocaleDateString('pt-BR')
}

function ConsultaNfMomentum() {
  const { data: itens = [], isLoading, refetch, isRefetching } = useQuery(
    ['consulta-nf-momentum'],
    obterNfsMomentum
  )

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Consulta NF Momentum</h1>
          <p className="mt-1 text-sm text-gray-600">
            Notas fiscais do sistema Momentum (análise de impostos)
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="inline-flex items-center px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium disabled:opacity-70"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`}
            />
            Atualizar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500 mx-auto" />
            <p className="mt-3 text-gray-600">Carregando notas fiscais...</p>
          </div>
        ) : itens.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum registro encontrado.</p>
            <p className="mt-1 text-sm text-gray-500">
              Verifique a configuração do banco Momentum ou tente novamente mais tarde.
            </p>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm text-gray-600">
              {itens.length} registro(s)
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">CNPJ</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">NF</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Valor Bruto</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Status Análise</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Data Cad.</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Compromisso</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Aprovado</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Data Venc.</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 max-w-[220px]">Justificativa</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {itens.map((row, idx) => (
                    <tr key={`${row.nf}-${row.cnpj}-${idx}`} className="hover:bg-gray-50/80">
                      <td className="px-4 py-3 text-gray-900 tabular-nums">{formatarCnpj(row.cnpj)}</td>
                      <td className="px-4 py-3 text-gray-900">{row.nf}</td>
                      <td className="px-4 py-3 text-gray-900 tabular-nums whitespace-nowrap">
                        {formatarMoeda(row.valorBruto)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{row.statusAnalise}</td>
                      <td className="px-4 py-3 text-gray-700">{formatarData(row.dataDeCadastro)}</td>
                      <td className="px-4 py-3 text-gray-700">{row.compromisso ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{row.aprovado ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{formatarData(row.dataDoVencimento)}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[220px] truncate" title={row.justificativa ?? undefined}>
                        {row.justificativa ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default ConsultaNfMomentum
